
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls";
import toast from "react-hot-toast";

import { formatDate, formatTime } from "../../../Utils/DateAndTime";

import { useNavigate, useSearchParams } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { isValidFilterValue } from "../../../Components/filters/FilterService";
import {
  buildListRequestBody,
  getListAuthHeaders,
  pruneStaleFilterSearchParams,
  applyDefaultSearchParams,
} from "../utils/listFilters";
import useModuleListConfig from "../../../Hooks/useModuleListConfig";

const FILTER_MODULE = "organization_project_tasks";

function ProjectTasksList() {
  const navigate = useNavigate();
  const { userData, reporting_manager_employees } = useAuthStore();
  // Different backends/versions return org in different shapes.
  // Normalize here so filters/pagination always use the correct org id.
  const orgId =
    userData?.organization?.organization_id ??
    userData?.user?.organization_id ??
    userData?.organization_id ??
    null;
  const [searchParams, setSearchParams] = useSearchParams();
  const { config: listConfig } = useModuleListConfig(FILTER_MODULE, "employee");

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  // Filters applied via FilterBuilder (used when URL sync is correct; avoids re-parse gaps)
  const appliedFiltersRef = useRef([]);

  const CopyFunction = async (id) => {
    // 1️⃣ Show loading toast
    const loadingToast = toast.loading("Copying the task, please wait...");

    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${MAIN_URL}/api/project-management/project-tasks/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = res.data?.data;

      if (data) {

        let finalStructured = {
          id:data?.organization_project_task_id,
  
          organization_id: data.organization_id || "",
          organization_entity_id: data.organization_entity_id || "",
          organization_project_id: data.organization_project_id || "",
          organization_project_milestone_id: data.organization_project_milestone_id || "",
          organization_project_task_template_id: data.organization_project_task_template_id || "",
          organization_project_task_category_id: data.organization_project_task_category_id || "",
          organization_project_task_subcategory_id: data.organization_project_task_subcategory_id || "",
          priority: data.priority || "",
          task_title: data.task_title || "",
          parent_task_id: data.parent_task_id || "",
          organization_project_task_type_id: data.organization_project_task_type_id || "",
          complexity_level: data.complexity_level || "",
          quantity: data.quantity || "",
          estimated_minutes: data.estimated_minutes || "",
          assigned_employee_id: data.assigned_employee_id || "",
          scheduled_date: formatDate(data.scheduled_date) || "",
          scheduled_time: formatTime(data.scheduled_time) || "",
          status_remarks: data.status_remarks || "",
          schedules: (data.schedules || []).map(sc => ({

            organization_project_task_schedule_id: null,
            organization_id: sc.organization_id || "",
            'organization_entity_id': null,
            'organization_project_task_id': sc.organization_project_task_id || null,
            'scheduled_date': (sc.scheduled_date || "").split("T")[0] || null,
            'allocated_minutes': sc.allocated_minutes,
            'is_reassigned': sc.is_reassigned,
            'task_status': sc.task_status,
            'status_remarks': sc.status_remarks

          }))
        };

        // Save to localStorage
        // localStorage.setItem("pro-task", JSON.stringify(finalStructured));

        // 2️⃣ Remove loading toast
        toast.dismiss(loadingToast);
        toast.success("Task copied successfully!.");

        navigate("/project-task/add", { state: { copiedTask: finalStructured } });

        // 3️⃣ Show success toast
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Failed to fetch project task.");
    }
  };

  const ViewFunction = (obj) => {
    navigate(`/project-task/view/${obj?.id}`);
  };

  const deleteTask = async (id) => {
    await axios.delete(
      `${MAIN_URL}/api/project-management/project-tasks/${id}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  };

  // Initialize URL parameters with defaults on mount (same pattern as EmployeesList)
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const { params: currentParams, needsUpdate } = applyDefaultSearchParams(
      searchParams,
      listConfig
    );

    if (needsUpdate) {
      setSearchParams(currentParams, { replace: true });
    }

    hasInitializedURL.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drop stale filter URL params (e.g. filter_project=undefined with only a _label left over)
  useEffect(() => {
    if (!hasInitializedURL.current) return;
    const { params, needsUpdate } = pruneStaleFilterSearchParams(searchParams);
    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Load datagrid configuration (same logic as CommonList)
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!orgId) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: getListAuthHeaders(),
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Project_tasks_grid_${orgId}`;
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey);

          if (savedConfig?.datagrid_default_configuration) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            if (Array.isArray(serverCfg.columns) && serverCfg.columns.length > 0) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(null);
            }
          } else {
            setConfigColumns(null);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(null);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [orgId]);

  const fetchProjectTasksWithFilters = useCallback(async () => {
    if (!orgId) return;

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    fetchControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const extra =
        Array.isArray(reporting_manager_employees) &&
        reporting_manager_employees.length > 0
          ? { organization_person_ids: reporting_manager_employees }
          : {};

      const requestBody = buildListRequestBody({
        searchParams,
        orgId,
        listConfig,
        appliedFiltersRef,
        extra,
      });

      const filteredUrl =
        listConfig.filteredEndpoint ||
        "/api/project-management/project-tasks/filtered";

      const response = await fetch(
        `${MAIN_URL}${filteredUrl}`,
        {
          method: "POST",
          headers: getListAuthHeaders(),
          body: JSON.stringify(requestBody),
          signal: fetchControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        const normalized = result.data.map((item) => ({
          ...item,
          // Ensure id exists for table row operations
          id: item.organization_project_task_id ?? item.id,
          // Fallback aliases for datagrid configs that expect metadata-style field keys.
          // If backend metadata labels are not present yet, at least show usable values (ids/titles)
          // instead of rendering "undefined" everywhere.
          project_task_name: item.project_task_name ?? item.task_title,
          project: item.project ?? item.organization_project_id,
          task_category:
            item.task_category ?? item.organization_project_task_category_id,
          task_sub_category:
            item.task_sub_category ??
            item.organization_project_task_subcategory_id,
          assigned_to_employee:
            item.assigned_to_employee ?? item.assigned_organization_person_id,
          // Some configs name this column `assigned_employee`
          assigned_employee:
            item.assigned_employee ??
            item.assigned_to_employee ??
            item.assigned_organization_person_id,
          // Some saved configs use truncated/variant accessors
          assigned_employ:
            item.assigned_employ ??
            item.assigned_employee ??
            item.assigned_to_employee ??
            item.assigned_organization_person_id,
          assigned_employe:
            item.assigned_employe ??
            item.assigned_employee ??
            item.assigned_to_employee ??
            item.assigned_organization_person_id,

          // Additional common datagrid keys used in Project Tasks views
          project_milestone:
            item.project_milestone ?? item.organization_project_milestone_id,
          // Some configs use `milestone` instead of `project_milestone`
          milestone:
            item.milestone ??
            item.project_milestone ??
            item.organization_project_milestone_id,
          parent_task: item.parent_task ?? item.parent_task_id,
          task_type: item.task_type ?? item.organization_project_task_type_id,
          complexity: item.complexity ?? item.complexity_level,
          allocated_minutes:
            item.allocated_minutes ??
            item.total_estimated_minutes ??
            item.estimated_minutes,

          // More keys commonly present in saved datagrid configs for project tasks
          template:
            item.template ?? item.organization_project_task_template_id,
          task_template:
            item.task_template ?? item.organization_project_task_template_id,
          team: item.team ?? item.organization_project_team_id,
          project_team: item.project_team ?? item.organization_project_team_id,

          // Sometimes configs expect these label-like keys
          assigned_employee:
            item.assigned_employee ?? item.assigned_organization_person_id,
          organization_person:
            item.organization_person ?? item.assigned_organization_person_id,
          organization_person_id:
            item.organization_person_id ?? item.assigned_organization_person_id,

          // Total calculated minutes is not returned by this endpoint; provide a safe fallback
          total_calculated_minutes:
            item.total_calculated_minutes ??
            item.actual_minutes_taken ??
            0,

          // More accessor fallbacks seen in some saved column configs
          assigned_employee_id:
            item.assigned_employee_id ?? item.assigned_organization_person_id,
          organization_persons:
            item.organization_persons ?? item.assigned_organization_person_id,
          status_remark:
            item.status_remark ?? item.status_remarks ?? item.task_remarks,
          statusRemarks:
            item.statusRemarks ?? item.status_remarks ?? item.task_remarks,
        }));
        setTasks(normalized);

        if (result.pagination) {
          const pageSize = result.pagination.pageSize || result.pagination.limit || 10;
          const requestedPage = Number.parseInt(searchParams.get("page"), 10) || 1;
          setPaginationData({
            page: result.pagination.page || requestedPage,
            pageSize,
            total: result.pagination.total || 0,
            totalPages: result.pagination.totalPages || 1,
            hasNext: result.pagination.hasNext || false,
            hasPrev: result.pagination.hasPrev || false,
          });
        } else {
          setPaginationData({
            page: 1,
            pageSize: 10,
            total: normalized.length,
            totalPages: 1,
            hasNext: false,
            hasPrev: false,
          });
        }
      } else {
        setTasks([]);
        setPaginationData({
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        });
        toast.error(result.message || result.error || "Failed to fetch project tasks");
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Error fetching project tasks:", err);
      toast.error("Failed to fetch project tasks");
      setTasks([]);
      setPaginationData({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, reporting_manager_employees, searchParams, listConfig]);

  useEffect(() => {
    fetchProjectTasksWithFilters();
    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [fetchProjectTasksWithFilters]);

  const handleFilterChange = useCallback((builderFilters) => {
    if (!Array.isArray(builderFilters)) {
      appliedFiltersRef.current = [];
      return;
    }
    appliedFiltersRef.current = builderFilters.filter(
      (f) =>
        f?.field &&
        f?.operator &&
        (!operatorRequiresValue(f.operator) || isValidFilterValue(f.value))
    );
  }, []);

  return (
    <>
      <Layout4
        heading="Employee Project Tasks"
        Route="/project-task"
        btnName="Add Project Task"
        showAddButton={true}
        add_action="ADD_EMPLOYEE_TASK"
      />

      {loading || loadingConfig ? (
        <div style={{ padding: 16 }}>Loading...</div>
      ) : (
        <TableDataGeneric
          onFilterChange={handleFilterChange}
          copyFunction={(obj) => CopyFunction(obj?.id)}
          copy_action="COPY_EMPLOYEE_TASK"
          viewFunction={(obj) => ViewFunction(obj)}
          gridKey="Project_tasks_grid"
          tableName="organization_project_tasks"
          // FilterBuilder hits /api/metadata/{module}. Backend module id is plural.
          filterModule={FILTER_MODULE}
          primaryKey="id"
          heading="organization_project_tasks"
          data={tasks}
          sortname={"scheduled_date"}
          showActions={true}
          Route="/project-task"
          DeleteFunc={deleteTask}
          token={localStorage.getItem("token")}
          configss={configColumns}
          edit_delete_action={["EDIT_EMPLOYEE_TASK", "DELETE_EMPLOYEE_TASK"]}
          paginationData={paginationData}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  )


}

export default ProjectTasksList

